import './Facets.scss';
import React, {useCallback} from 'react';
import {FacetsHeader} from './FacetsHeader/FacetsHeader';
import {FacetsContent} from './FacetsContent/FacetsContent';
import {HibuButton} from '../common/hibuButton/hibuButton';
import {Icons} from '../icons/icons';
import {SvgIcon} from '../icons/svgIcon';
import {useDispatch} from 'react-redux';
import {useIsFiltersPending, useIsShowFilters} from './duck/filter.hooks';
import {changeVisibility} from './duck/filter.actions';
import {Spinner} from '../common/spinner/spinner';
import classNames from 'clsx';
import {DrawerProps, HibuDrawer} from '../common/drawer/hibuDrawer';
import {useTranslation} from 'react-i18next';

type FacetsProps = {
    showSidebar: boolean;
    closeDrawer: () => void;
};

export const Facets = React.memo(({showSidebar, closeDrawer}: FacetsProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const showFilters: boolean = useIsShowFilters();
    const filtersPending: boolean = useIsFiltersPending();

    const onExpand = useCallback(() => {
        dispatch(changeVisibility(!showFilters));
    }, [dispatch, showFilters]);

    const headerMobile = ({onCloseRequest}: DrawerProps) => (
        <SvgIcon
            className="details-header-close"
            icon={Icons.X}
            onClick={onCloseRequest}
            alt={t('general.close')}
        />
    );

    const content = useCallback(() => {
        return (
            <div id="facets" className={filtersPending ? 'fetch-pending' : ''}>
                <FiltersPendingSpinner filtersPending={filtersPending}/>
                <FiltersCollapsed showFilters={showFilters} onExpand={onExpand}/>
                <FiltersExpanded showFilters={showFilters} onExpand={onExpand}/>
            </div>
        );
    }, [showFilters, filtersPending, onExpand]);

    return (
        <HibuDrawer
            id={'hibu_facet_column'}
            className={classNames(showFilters ? '' : 'hideContent')}
            show={showSidebar}
            onCloseRequest={closeDrawer}
            headerCbMobile={headerMobile}
            contentCb={content}
        />
    );
});

type FiltersPendingProps = {
    filtersPending: boolean;
};

const FiltersPendingSpinner = ({filtersPending}: FiltersPendingProps) => {
    if (!filtersPending) return null;
    return (
        <Spinner/>
    );
};

type ShowFiltersProps = {
    showFilters: boolean;
    onExpand: () => void;
};

const FiltersCollapsed = ({showFilters, onExpand}: ShowFiltersProps) => {
    if (showFilters) return null;
    return (
        <button id="filters-collapsed" onClick={onExpand}>
            <SvgIcon
                icon={Icons.FILTER}
                className={'facet-list-collapsed-filter'}
            />
            <SvgIcon
                icon={Icons.ARROWS}
                className={'facet-list-collapsed-arrows'}
            />
        </button>
    );
};

const FiltersExpanded = ({showFilters, onExpand}: ShowFiltersProps) => {
    if (!showFilters) return null;
    return (
        <div id="facets-inner">
            <FacetsHeader/>
            <FacetsContent/>
            <HideFiltersButton onExpand={onExpand}/>
        </div>
    );
};

type HideFiltersProps = {
    onExpand: () => void;
};

const HideFiltersButton = ({onExpand}: HideFiltersProps) => (
    <HibuButton
        size={'sm'}
        onClick={onExpand}
        icon={Icons.ARROWS}
        tooltipKey={'search.details.hideDetails'}
        visible={true}
        className={'facet-list-close'}
        placement={'right'}
    />
);
